import { defineStore } from 'pinia';
import { authApi } from '@/api/authApi.ts';
import {
    computed, ref,
} from 'vue';
import { IUserAccess } from '@/interfaces/auth/IUserAccess.ts';
import { IDashboardModuleAccess, IPowerMarketsAccess } from '@/interfaces/auth/IPowerMarketsGbAccess.ts';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { defaultEnactPagesAccess, IEnactPagesAccess } from '@/interfaces/auth/IEnactPagesAccess.ts';
import { IStorageTrackingAccess } from '@/interfaces/auth/IStorageTrackingAccess.ts';
import {
    IIsTrialAndReadOnly, IStorageForecastingAccess, IStorageForecastingAccessSortedByAddOn, IStorageForecastingAddOnWithCountries,
} from '@/interfaces/auth/IStorageForecastingAccess.ts';
import { authState } from '@lcp/http-auth';
import { defaultUserAccess } from '@/helpers/access/accessHelper.ts';
import { StorageForecastingAddOns } from '@/enums/storageForecasting/StorageForecastingAddOnsEnum.ts';
import { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { storageTrackingAccessApi } from '@/api/storageTracking/StorageTrackingAccessApi.ts';
import { longTermForecastingAccessApi } from '@/api/storageForecasting/LongTermForecastingAccessApi.ts';
import {
    mapGeneralAccess,
    mapShortTermPowerAccess, mapStorageForecastingAccess, mapStorageTrackingAccess, mergePageAccess,
} from '@/utils/accessMapper.ts';
import { IFlexIndexAccess } from '@/interfaces/benchmarks/flexIndex/IFlexIndexAccess.ts';

export const useUserAccessStore = defineStore('userAccessStore', () => {
    const userAccess = ref<IUserAccess>(defaultUserAccess);
    const canCreateCommunityThread = computed<boolean>(() => userAccess.value.isAdmin);

    const enactPageAccess = ref<IEnactPagesAccess>(defaultEnactPagesAccess);
    const pageAccessLoaded = ref<boolean>(false);
    const dashboardModuleAccess = ref<IDashboardModuleAccess | null>(null);
    let generalAccessTask: Promise<void> | null = null;
    let shortTermAccessTaskPromise: Promise<void> | null = null;
    const flexIndexAccess = ref<IFlexIndexAccess | null>(null);

    const storageTrackingConfig = computed<IStorageTrackingAccess | null>(() => userAccess.value.enact.assetTracking);
    const shortTermMarketsConfig = computed<IPowerMarketsAccess | null>(() => userAccess.value.enact.shortTermMarkets);

    const storageForecastingConfig = computed<IStorageForecastingAccessSortedByAddOn | null>(() => convertToSortedByAddOn(userAccess.value.enact.longTermMarkets));
    const isRunningAsDifferentUser = ref<boolean>(false);

    const assetTrackingIsTrialOrNoAccessOrMarketTrendsOnly = computed<boolean>(() => (storageTrackingConfig.value?.isTrial || !storageTrackingConfig.value || storageTrackingConfig.value?.marketTrendsOnlyAccess) ?? false);

    function convertToSortedByAddOn (access: IStorageForecastingAccess | null): IStorageForecastingAccessSortedByAddOn | null {
        if (!access) return null;

        const addOnAccess: Partial<Record<StorageForecastingAddOns, IStorageForecastingAddOnWithCountries>> = {};
        access.countryAccess.forEach(countryAccess => {
            const country = countryAccess.country;
            countryAccess.addOns.forEach(addOnEntry => {
                const addOn = addOnEntry.addOn;
                if (!addOnAccess[addOn]) {
                    addOnAccess[addOn] = {
                        addOn,
                        countries: {},
                    };
                }
                addOnAccess[addOn].countries[country] = {
                    isTrial: addOnEntry.isTrial,
                    isReadOnly: addOnEntry.isReadOnly,
                };
            });
        });

        return {
            isAdmin: access.isAdmin,
            isLcpUser: access.isLcpUser,
            monthlyMaxRuns: access.monthlyMaxRuns,
            maxAssetCount: access.maxAssetCount,
            addOnAccess,
        };
    }

    function hasAccess<T> (section: T | null): section is T {
        return section != null;
    }

    function userHasCountryAccessforStorageForecastingAddOn (addOn: StorageForecastingAddOns, country: TStorageForecastCountries | null): boolean {
        if (!country) return false;
        const countriesOnAddOn = storageForecastingConfig.value?.addOnAccess[addOn];
        if (!countriesOnAddOn) return false;

        return Object.keys(countriesOnAddOn.countries).includes(country);
    }

    function userIsReadOnlyForStorageForecastingAddOnCountryCombination (addOn: StorageForecastingAddOns, country: TStorageForecastCountries | null): boolean {
        if (!country) return true;
        const countriesOnAddOn = storageForecastingConfig.value?.addOnAccess[addOn];
        if (!countriesOnAddOn) return true;

        return countriesOnAddOn.countries[country]?.isReadOnly ?? true;
    }

    function userIsTrialForStorageForecastingAddOnCountryCombination (addOn: StorageForecastingAddOns, country: TStorageForecastCountries | null): boolean {
        if (!country) return true;
        const countriesOnAddOn = storageForecastingConfig.value?.addOnAccess[addOn];
        if (!countriesOnAddOn) return true;

        return countriesOnAddOn.countries[country]?.isTrial ?? true;
    }

    function getFirstOrDefaultStorageForecastingCountry (addOn: StorageForecastingAddOns): TStorageForecastCountries {
        const countriesWithAccess: Partial<Record<TStorageForecastCountries, IIsTrialAndReadOnly>> | null = storageForecastingConfig.value?.addOnAccess[addOn]?.countries ?? null;
        if (countriesWithAccess === null || Object.keys(countriesWithAccess).length === 0) return 'GB';

        const firstNonReadonlyNonTrialCompany: TStorageForecastCountries | null = Object.entries(countriesWithAccess).find(([, v]) => v?.isTrial === false && v.isReadOnly === false)?.[0] as TStorageForecastCountries ?? null;

        return (firstNonReadonlyNonTrialCompany ?? Object.keys(countriesWithAccess)[0]);
    }

    function acceptAiWarning () : void {
        deltaGptApi.acceptAiWarning();
        userAccess.value.research.acceptedAiWarning = true;
    }

    async function getUserAccess (shortTermPowerAccessTask: Promise<void>): Promise<void> {
        if (!authState.value.loggedIn) return;

        try {
            await ensureGeneralAccessLoaded();
            const accessTasks = [
                getResearchAccess(),
                getAssetTrackingAccess(),
                getLongTermForecastingAccess(),
                shortTermPowerAccessTask,
                getFlexIndexAccess(),
            ];
            const result = await Promise.allSettled(accessTasks);
            if (result.some(r => r.status === 'rejected')) {
                console.error('Error fetching some access data: ', result.filter(r => r.status === 'rejected'));
            }

            pageAccessLoaded.value = true;
        } catch (err) {
            console.log(err);
        }
    }

    function startAccessLoading (): { shortTermPowerAccessTask: Promise<void>; allAccessTask: Promise<void> } {
        const shortTermAccessTask = ensureShortTermPowerAccessLoaded();
        const allAccessTask = getUserAccess(shortTermAccessTask);

        return { shortTermPowerAccessTask: shortTermAccessTask, allAccessTask };
    }

    function ensureShortTermPowerAccessLoaded (): Promise<void> {
        if (!shortTermAccessTaskPromise) {
            shortTermAccessTaskPromise = ensureGeneralAccessLoaded()
                .then(() => getShortTermPowerAccess())
                .catch((error: unknown) => {
                    shortTermAccessTaskPromise = null;
                    throw error;
                });
        }

        return shortTermAccessTaskPromise;
    }

    function ensureGeneralAccessLoaded (): Promise<void> {
        if (!generalAccessTask) {
            generalAccessTask = getGeneralAccess().catch(error => {
                generalAccessTask = null;
                throw error;
            });
        }

        return generalAccessTask;
    }

    async function getGeneralAccess (): Promise<void> {
        const generalAccess = await authApi.getGeneralAccess();
        userAccess.value.isLcp = generalAccess.config.isLcp;
        userAccess.value.isAdmin = generalAccess.config.isAdmin;
        userAccess.value.isAdminRequestApprover = generalAccess.config.isAdminRequestApprover;
        isRunningAsDifferentUser.value = generalAccess.config.runningAsDifferentUser;
        userAccess.value.enact.acceptedEnactTerms = generalAccess.config.acceptedEnactTerms;

        const mappedPageAccess = mapGeneralAccess(generalAccess.pages);

        mergePageAccess(enactPageAccess.value, mappedPageAccess);
    }

    async function getResearchAccess (): Promise<void> {
        const researchAccess = await deltaGptApi.getUserAccess();
        userAccess.value.research = researchAccess;
    }

    async function getAssetTrackingAccess (): Promise<void> {
        const assetTrackingAccess = await storageTrackingAccessApi.getUserAccess();
        userAccess.value.enact.assetTracking = assetTrackingAccess.config;

        const mappedPageAccess = mapStorageTrackingAccess(assetTrackingAccess.pages);

        mergePageAccess(enactPageAccess.value, mappedPageAccess);
    }

    async function getLongTermForecastingAccess (): Promise<void> {
        const longTermForecastingAccess = await longTermForecastingAccessApi.getUserAccess();
        userAccess.value.enact.longTermMarkets = longTermForecastingAccess.config;

        const mappedPageAccess = mapStorageForecastingAccess(longTermForecastingAccess.pages, longTermForecastingAccess.config);

        mergePageAccess(enactPageAccess.value, mappedPageAccess);
    }

    async function getShortTermPowerAccess (): Promise<void> {
        const shortTermPowerAccess = await authApi.getUserAccess();
        userAccess.value.enact.shortTermMarkets = shortTermPowerAccess.config;
        dashboardModuleAccess.value = shortTermPowerAccess.dashboardModules;

        // Only map Spark-provided Enact page access when the rollout capability is enabled.
        const mappedPageAccess = shortTermPowerAccess.config?.hasSparkUIAccess
            ? mapShortTermPowerAccess(shortTermPowerAccess.pages)
            : mapShortTermPowerAccess(defaultEnactPagesAccess);

        mergePageAccess(enactPageAccess.value, mappedPageAccess);
    }

    async function getFlexIndexAccess (): Promise<void> {
        flexIndexAccess.value = await authApi.getFlexIndexAccess();
        userAccess.value.enact.residential = {
            hasAccess: flexIndexAccess.value.hasAccess,
            isAdmin: flexIndexAccess.value.isAdmin,
        };
        enactPageAccess.value['flex-index'] = { hasAccess: flexIndexAccess.value.hasAccess };
    }

    return {
        userAccess,
        canCreateCommunityThread,
        enactPageAccess,
        flexIndexAccess,
        storageTrackingConfig,
        storageForecastingConfig,
        shortTermMarketsConfig,
        getFirstOrDefaultStorageForecastingCountry,
        acceptAiWarning,
        pageAccessLoaded,
        dashboardModuleAccess,
        hasAccess,
        userIsReadOnlyForStorageForecastingAddOnCountryCombination,
        userIsTrialForStorageForecastingAddOnCountryCombination,
        userHasCountryAccessforStorageForecastingAddOn,
        isRunningAsDifferentUser,
        getLongTermForecastingAccess,
        assetTrackingIsTrialOrNoAccessOrMarketTrendsOnly,
        ensureGeneralAccessLoaded,
        ensureShortTermPowerAccessLoaded,
        startAccessLoading,
    };
});
